import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

import FirebaseImage from '../../Components/Media/FirebaseImage';
import Modal from '../../Components/UI/Modal/Modal';
import { uploadPortfolioImage } from '../../Utils/uploads';
import {
  getPortfolioBeforeAfter,
  getPortfolioCategory,
  getPortfolioMedia,
  getPortfolioSummary,
  getPortfolioTitle,
  PORTFOLIO_MEDIA_SOURCE,
  PORTFOLIO_PHASE,
  PORTFOLIO_PHASE_LABELS,
  PORTFOLIO_STATUS,
} from '../../Utils/portfolio';

const defaultForm = {
  title: '',
  summary: '',
  category: '',
  city: '',
  duration: '',
  techniques: '',
  result: '',
  priceRange: '',
  status: PORTFOLIO_STATUS.DRAFT,
  featured: false,
};

const defaultComparison = {
  enabled: false,
  title: 'Avant / Après',
  beforeId: '',
  afterId: '',
};

const PortfolioProjectEditor = ({ opened, project, saving, onClose, onSave }) => {
  const [form, setForm] = useState(defaultForm);
  const [media, setMedia] = useState([]);
  const [coverImage, setCoverImage] = useState('');
  const [comparison, setComparison] = useState(defaultComparison);
  const [uploading, setUploading] = useState(false);
  const [uploadState, setUploadState] = useState({ current: 0, total: 0, percent: 0, name: '' });
  const [error, setError] = useState('');

  const clientMedia = useMemo(
    () => media.filter((item) => item.source === PORTFOLIO_MEDIA_SOURCE.CLIENT),
    [media],
  );
  const artisanMedia = useMemo(
    () => media.filter((item) => item.source === PORTFOLIO_MEDIA_SOURCE.ARTISAN),
    [media],
  );

  useEffect(() => {
    if (!opened || !project) return;
    const portfolio = project.portfolio || {};
    const currentMedia = getPortfolioMedia(project);
    const beforeAfter = getPortfolioBeforeAfter(project);

    setForm({
      title: getPortfolioTitle(project),
      summary: getPortfolioSummary(project),
      category: getPortfolioCategory(project),
      city: portfolio.city || '',
      duration: portfolio.duration || '',
      techniques: portfolio.techniques || '',
      result: portfolio.result || '',
      priceRange: portfolio.price_range || '',
      status: [PORTFOLIO_STATUS.DRAFT, PORTFOLIO_STATUS.PUBLISHED, PORTFOLIO_STATUS.HIDDEN]
        .includes(portfolio.status)
        ? portfolio.status
        : PORTFOLIO_STATUS.DRAFT,
      featured: Boolean(portfolio.featured),
    });
    setMedia(currentMedia);
    setCoverImage(portfolio.cover_image || currentMedia[0]?.url || '');
    setComparison(beforeAfter ? {
      enabled: true,
      title: beforeAfter.title || 'Avant / Après',
      beforeId: beforeAfter.before.id,
      afterId: beforeAfter.after.id,
    } : defaultComparison);
    setUploading(false);
    setUploadState({ current: 0, total: 0, percent: 0, name: '' });
    setError('');
  }, [opened, project]);

  const updateField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateMediaPhase = (mediaId, phase) => {
    setMedia((current) => current.map((item) => (
      item.id === mediaId ? { ...item, phase } : item
    )));
  };

  const chooseComparisonImage = (field, mediaId) => {
    setComparison((current) => ({ ...current, [field]: mediaId }));
    if (mediaId) {
      updateMediaPhase(
        mediaId,
        field === 'beforeId' ? PORTFOLIO_PHASE.BEFORE : PORTFOLIO_PHASE.AFTER,
      );
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0 || !project?.demand_id || uploading || saving) return;

    if (files.length > 6) {
      setError('Ajoutez au maximum 6 photos à la fois.');
      return;
    }
    if (media.length + files.length > 24) {
      setError('Une réalisation peut contenir au maximum 24 photos.');
      return;
    }

    setUploading(true);
    setError('');
    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        setUploadState({ current: index + 1, total: files.length, percent: 0, name: file.name });
        const item = await uploadPortfolioImage(file, project.demand_id, (percent) => {
          setUploadState({ current: index + 1, total: files.length, percent, name: file.name });
        });
        setMedia((current) => (
          current.some((existing) => existing.url === item.url) ? current : [...current, item]
        ));
        setCoverImage((current) => current || item.url || '');
      }
    } catch (uploadError) {
      setError(uploadError?.message || 'Le téléchargement des photos a échoué.');
    } finally {
      setUploading(false);
      setUploadState({ current: 0, total: 0, percent: 0, name: '' });
    }
  };

  const removeArtisanMedia = (mediaId) => {
    const target = media.find((item) => item.id === mediaId);
    if (!target || target.source !== PORTFOLIO_MEDIA_SOURCE.ARTISAN) return;

    const next = media.filter((item) => item.id !== mediaId);
    setMedia(next);
    if (coverImage === target.url) setCoverImage(next[0]?.url || '');
    setComparison((current) => ({
      ...current,
      beforeId: current.beforeId === mediaId ? '' : current.beforeId,
      afterId: current.afterId === mediaId ? '' : current.afterId,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.title.trim()) {
      setError('Ajoutez un titre clair pour présenter cette réalisation.');
      return;
    }
    if (!form.summary.trim()) {
      setError('Ajoutez un court résumé du chantier.');
      return;
    }
    if (form.status === PORTFOLIO_STATUS.PUBLISHED && media.length === 0) {
      setError('Ajoutez au moins une photo avant de publier.');
      return;
    }
    if (comparison.enabled && (!comparison.beforeId || !comparison.afterId)) {
      setError('Choisissez une photo « avant » et une photo « après ».');
      return;
    }
    if (comparison.enabled && comparison.beforeId === comparison.afterId) {
      setError('Les photos « avant » et « après » doivent être différentes.');
      return;
    }

    const success = await onSave?.({
      ...form,
      media,
      selectedImages: media.map((item) => item.url),
      coverImage,
      beforeAfter: comparison,
    });
    if (success) onClose?.();
  };

  const busy = Boolean(saving || uploading);

  return (
    <Modal opened={opened} close={busy ? undefined : onClose}>
      <EditorHeader>
        <div>
          <Eyebrow>Éditeur de vitrine</Eyebrow>
          <h2>Présenter cette réalisation</h2>
          <p>Les photos du client sont conservées. Vous pouvez ajouter vos propres photos et créer une comparaison avant/après.</p>
        </div>
        <IconButton type="button" aria-label="Fermer" onClick={onClose} disabled={busy}>
          <CloseOutlinedIcon />
        </IconButton>
      </EditorHeader>

      <Form onSubmit={submit}>
        <Section>
          <SectionTitle>
            <ImageOutlinedIcon />
            <div>
              <strong>Galerie du projet</strong>
              <span>Chaque photo indique clairement son origine et son rôle dans le chantier.</span>
            </div>
          </SectionTitle>

          <UploadPanel>
            <div>
              <ConstructionOutlinedIcon />
              <span>
                <strong>Ajouter des photos de l’artisan</strong>
                <small>JPG, PNG ou WebP · 8 Mo maximum par photo · 6 photos à la fois</small>
              </span>
            </div>
            <UploadButton $disabled={busy}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                disabled={busy}
                onChange={handleUpload}
              />
              <CloudUploadOutlinedIcon />
              {uploading
                ? `${uploadState.current}/${uploadState.total} · ${uploadState.percent}%`
                : 'Ajouter des photos'}
            </UploadButton>
          </UploadPanel>

          {uploading && (
            <UploadProgress aria-live="polite">
              <div><span style={{ width: `${uploadState.percent}%` }} /></div>
              <small>Téléchargement de {uploadState.name || 'la photo'}…</small>
            </UploadProgress>
          )}

          <MediaGroup>
            <MediaGroupHeader>
              <div><PersonOutlineIcon /><strong>Photos envoyées par le client</strong></div>
              <span>{clientMedia.length} photo{clientMedia.length > 1 ? 's' : ''} · conservées</span>
            </MediaGroupHeader>
            {clientMedia.length === 0 ? (
              <EmptyImages>Aucune photo client n’est disponible pour ce chantier.</EmptyImages>
            ) : (
              <ImageGrid>
                {clientMedia.map((item, index) => (
                  <MediaCard
                    key={item.id}
                    item={item}
                    index={index}
                    coverImage={coverImage}
                    onCover={setCoverImage}
                    onPhase={updateMediaPhase}
                  />
                ))}
              </ImageGrid>
            )}
          </MediaGroup>

          <MediaGroup>
            <MediaGroupHeader>
              <div><ConstructionOutlinedIcon /><strong>Photos ajoutées par l’artisan</strong></div>
              <span>{artisanMedia.length} photo{artisanMedia.length > 1 ? 's' : ''}</span>
            </MediaGroupHeader>
            {artisanMedia.length === 0 ? (
              <EmptyImages>Ajoutez des photos du résultat, des finitions ou des étapes du chantier.</EmptyImages>
            ) : (
              <ImageGrid>
                {artisanMedia.map((item, index) => (
                  <MediaCard
                    key={item.id}
                    item={item}
                    index={index}
                    coverImage={coverImage}
                    onCover={setCoverImage}
                    onPhase={updateMediaPhase}
                    onRemove={removeArtisanMedia}
                  />
                ))}
              </ImageGrid>
            )}
          </MediaGroup>
        </Section>

        <ComparisonSection>
          <ComparisonHeader>
            <div>
              <CompareArrowsOutlinedIcon />
              <span>
                <strong>Comparaison avant / après</strong>
                <small>Créez un curseur interactif visible dans la vitrine.</small>
              </span>
            </div>
            <SwitchLabel>
              <input
                type="checkbox"
                checked={comparison.enabled}
                onChange={(event) => setComparison((current) => ({
                  ...current,
                  enabled: event.target.checked,
                }))}
              />
              Activer
            </SwitchLabel>
          </ComparisonHeader>

          {comparison.enabled && (
            <ComparisonFields>
              <Field>
                <label htmlFor="before-photo">Photo avant</label>
                <select
                  id="before-photo"
                  value={comparison.beforeId}
                  onChange={(event) => chooseComparisonImage('beforeId', event.target.value)}
                >
                  <option value="">Choisir une photo</option>
                  {media.map((item, index) => (
                    <option key={item.id} value={item.id} disabled={item.id === comparison.afterId}>
                      {item.source === PORTFOLIO_MEDIA_SOURCE.CLIENT ? 'Client' : 'Artisan'} · Photo {index + 1}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <label htmlFor="after-photo">Photo après</label>
                <select
                  id="after-photo"
                  value={comparison.afterId}
                  onChange={(event) => chooseComparisonImage('afterId', event.target.value)}
                >
                  <option value="">Choisir une photo</option>
                  {media.map((item, index) => (
                    <option key={item.id} value={item.id} disabled={item.id === comparison.beforeId}>
                      {item.source === PORTFOLIO_MEDIA_SOURCE.CLIENT ? 'Client' : 'Artisan'} · Photo {index + 1}
                    </option>
                  ))}
                </select>
              </Field>
              <Field $wide>
                <label htmlFor="comparison-title">Titre de la comparaison</label>
                <input
                  id="comparison-title"
                  value={comparison.title}
                  onChange={(event) => setComparison((current) => ({ ...current, title: event.target.value }))}
                  maxLength={80}
                  placeholder="Avant / Après"
                />
              </Field>
            </ComparisonFields>
          )}
        </ComparisonSection>

        <Fields>
          <Field $wide>
            <label htmlFor="portfolio-title">Titre public</label>
            <input
              id="portfolio-title"
              value={form.title}
              onChange={updateField('title')}
              maxLength={90}
              placeholder="Ex. Remplacement d’une baie vitrée"
            />
            <small>{form.title.length}/90</small>
          </Field>

          <Field $wide>
            <label htmlFor="portfolio-summary">Résumé du chantier</label>
            <textarea
              id="portfolio-summary"
              value={form.summary}
              onChange={updateField('summary')}
              maxLength={420}
              rows={4}
              placeholder="Décrivez le besoin, votre intervention et le résultat obtenu."
            />
            <small>{form.summary.length}/420</small>
          </Field>

          <Field>
            <label htmlFor="portfolio-category">Catégorie</label>
            <input id="portfolio-category" value={form.category} onChange={updateField('category')} placeholder="Menuiserie" />
          </Field>
          <Field>
            <label htmlFor="portfolio-city">Ville ou zone</label>
            <input id="portfolio-city" value={form.city} onChange={updateField('city')} placeholder="Toulouse" />
          </Field>
          <Field>
            <label htmlFor="portfolio-duration">Durée</label>
            <input id="portfolio-duration" value={form.duration} onChange={updateField('duration')} placeholder="2 jours" />
          </Field>
          <Field>
            <label htmlFor="portfolio-price">Fourchette de prix (facultatif)</label>
            <input id="portfolio-price" value={form.priceRange} onChange={updateField('priceRange')} placeholder="500–1 000 €" />
          </Field>
          <Field $wide>
            <label htmlFor="portfolio-techniques">Techniques et matériaux</label>
            <input id="portfolio-techniques" value={form.techniques} onChange={updateField('techniques')} placeholder="Double vitrage, dépose sécurisée, étanchéité" />
          </Field>
          <Field $wide>
            <label htmlFor="portfolio-result">Résultat obtenu</label>
            <textarea id="portfolio-result" value={form.result} onChange={updateField('result')} rows={3} placeholder="Isolation restaurée et finition propre." />
          </Field>
        </Fields>

        <PublishPanel>
          <Field>
            <label htmlFor="portfolio-status">État de publication</label>
            <select id="portfolio-status" value={form.status} onChange={updateField('status')}>
              <option value={PORTFOLIO_STATUS.DRAFT}>Brouillon — visible uniquement par vous</option>
              <option value={PORTFOLIO_STATUS.PUBLISHED}>Publié — visible par les clients</option>
              <option value={PORTFOLIO_STATUS.HIDDEN}>Masqué — retiré temporairement</option>
            </select>
          </Field>
          <FeaturedLabel>
            <input
              type="checkbox"
              checked={form.featured}
              onChange={updateField('featured')}
              disabled={form.status !== PORTFOLIO_STATUS.PUBLISHED}
            />
            <span>
              <strong>Projet à la une</strong>
              <small>Un seul projet peut être mis en avant dans votre vitrine.</small>
            </span>
          </FeaturedLabel>
        </PublishPanel>

        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

        <Actions>
          <SecondaryButton type="button" onClick={onClose} disabled={busy}>Annuler</SecondaryButton>
          <PrimaryButton type="submit" disabled={busy}>
            {saving ? 'Enregistrement…' : uploading ? 'Téléchargement…' : form.status === PORTFOLIO_STATUS.PUBLISHED ? 'Enregistrer et publier' : 'Enregistrer'}
          </PrimaryButton>
        </Actions>
      </Form>
    </Modal>
  );
};

const MediaCard = ({ item, index, coverImage, onCover, onPhase, onRemove }) => {
  const isClient = item.source === PORTFOLIO_MEDIA_SOURCE.CLIENT;
  const isCover = coverImage === item.url;

  return (
    <ImageChoice $cover={isCover}>
      <ImageVisual>
        <FirebaseImage
          src={item.url}
          fallbackSrc="/images/project-placeholder.svg"
          alt={`Photo ${index + 1}`}
        />
        <SourceBadge $source={item.source}>
          {isClient ? <PersonOutlineIcon /> : <ConstructionOutlinedIcon />}
          {isClient ? 'Photo client' : 'Photo artisan'}
        </SourceBadge>
        {isCover && <CoverBadge><StarOutlineIcon /> Couverture</CoverBadge>}
      </ImageVisual>
      <ImageControls>
        <label>
          <span>Type de photo</span>
          <select value={item.phase || PORTFOLIO_PHASE.NONE} onChange={(event) => onPhase(item.id, event.target.value)}>
            {Object.entries(PORTFOLIO_PHASE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <CoverButton type="button" $active={isCover} onClick={() => onCover(item.url)}>
          {isCover ? <CheckCircleOutlineIcon /> : <StarOutlineIcon />}
          {isCover ? 'Photo de couverture' : 'Définir comme couverture'}
        </CoverButton>
        {isClient ? (
          <LockedNote><LockOutlinedIcon /> Conservée avec le projet client</LockedNote>
        ) : (
          <RemoveButton type="button" onClick={() => onRemove?.(item.id)}>
            <DeleteOutlineIcon /> Retirer de la vitrine
          </RemoveButton>
        )}
      </ImageControls>
    </ImageChoice>
  );
};

const EditorHeader = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1.4rem;
  h2 { margin: 0; color: #111111; font-size: clamp(1.45rem, 4vw, 2rem); }
  p { max-width: 34rem; margin: .45rem 0 0; color: #66615b; line-height: 1.55; }
`;
const Eyebrow = styled.div`margin-bottom: .35rem; color: #a88a5d; font-size: .72rem; font-weight: 900; letter-spacing: .11em; text-transform: uppercase;`;
const IconButton = styled.button`width: 2.5rem; height: 2.5rem; display: grid; place-items: center; flex: 0 0 auto; border: 0; border-radius: 50%; background: #f1ece5; color: #29465a; cursor: pointer; &:disabled { opacity: .55; cursor: wait; }`;
const Form = styled.form`display: grid; gap: 1.25rem;`;
const Section = styled.section`display: grid; gap: 1rem;`;
const SectionTitle = styled.div`display: flex; align-items: center; gap: .7rem; color: #23465f; svg { font-size: 1.4rem; } div { display: grid; gap: .1rem; } span { color: #6b7983; font-size: .8rem; }`;
const UploadPanel = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1rem; border: 1px solid #dce6ec; border-radius: 1rem; background: #f5f9fb;
  > div { display: flex; align-items: center; gap: .7rem; } > div > svg { color: #0b527c; }
  span { display: grid; gap: .15rem; } small { color: #667985; line-height: 1.35; }
  @media (max-width: 36rem) { align-items: stretch; flex-direction: column; }
`;
const UploadButton = styled.label`
  position: relative; min-height: 2.8rem; display: inline-flex; align-items: center; justify-content: center; gap: .45rem; padding: .7rem 1rem; border-radius: .8rem; background: #111111; color: #fff; font-weight: 850; cursor: ${({ $disabled }) => ($disabled ? 'wait' : 'pointer')}; opacity: ${({ $disabled }) => ($disabled ? .6 : 1)};
  input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
`;
const UploadProgress = styled.div`
  display: grid; gap: .3rem; padding: .75rem; border-radius: .8rem; background: #eef6fa;
  > div { height: .5rem; overflow: hidden; border-radius: 999px; background: #dce8ee; } > div span { display: block; height: 100%; border-radius: inherit; background: #2b87b6; transition: width 160ms ease; }
  small { color: #526c7b; }
`;
const MediaGroup = styled.section`display: grid; gap: .7rem;`;
const MediaGroupHeader = styled.div`display: flex; align-items: center; justify-content: space-between; gap: .75rem; color: #29465a; div { display: flex; align-items: center; gap: .4rem; } svg { font-size: 1.1rem; } > span { color: #70808a; font-size: .73rem; } @media (max-width: 30rem) { align-items: flex-start; flex-direction: column; }`;
const EmptyImages = styled.div`padding: 1rem; border: 1px dashed #cbd7df; border-radius: .9rem; background: #f7f9fa; color: #66615b; text-align: center;`;
const ImageGrid = styled.div`display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .75rem; @media (max-width: 34rem) { grid-template-columns: 1fr; }`;
const ImageChoice = styled.article`min-width: 0; overflow: hidden; border: 2px solid ${({ $cover }) => ($cover ? '#d5a33f' : '#e1e8ec')}; border-radius: .95rem; background: #fff;`;
const ImageVisual = styled.div`position: relative; background: #f1ece5; img { width: 100%; aspect-ratio: 4 / 3; display: block; object-fit: contain; padding: .4rem; box-sizing: border-box; }`;
const SourceBadge = styled.span`
  position: absolute; left: .45rem; top: .45rem; display: inline-flex; align-items: center; gap: .28rem; padding: .32rem .48rem; border-radius: 999px; background: ${({ $source }) => ($source === PORTFOLIO_MEDIA_SOURCE.CLIENT ? 'rgba(0,0,0,.9)' : 'rgba(151,102,18,.92)')}; color: #fff; font-size: .65rem; font-weight: 850; backdrop-filter: blur(8px); svg { font-size: .9rem; }
`;
const CoverBadge = styled.span`position: absolute; right: .45rem; top: .45rem; display: inline-flex; align-items: center; gap: .25rem; padding: .32rem .46rem; border-radius: 999px; background: rgba(255,244,217,.96); color: #745318; font-size: .64rem; font-weight: 900; svg { font-size: .85rem; }`;
const ImageControls = styled.div`display: grid; gap: .5rem; padding: .65rem; label { display: grid; gap: .25rem; } label > span { color: #61737f; font-size: .68rem; font-weight: 800; } select { width: 100%; border: 1px solid #e7dfd2; border-radius: .62rem; background: #fff; color: #29465a; font: inherit; font-size: .75rem; padding: .55rem; }`;
const CoverButton = styled.button`width: 100%; min-height: 2.25rem; display: flex; align-items: center; justify-content: center; gap: .3rem; padding: .45rem; border: 1px solid ${({ $active }) => ($active ? '#e0b859' : '#dce4e9')}; border-radius: .65rem; background: ${({ $active }) => ($active ? '#fff4d9' : '#fff')}; color: ${({ $active }) => ($active ? '#765519' : '#526775')}; font: inherit; font-size: .69rem; font-weight: 850; cursor: pointer; svg { font-size: .9rem; }`;
const LockedNote = styled.span`display: flex; align-items: center; justify-content: center; gap: .3rem; color: #6c7a84; font-size: .66rem; svg { font-size: .82rem; }`;
const RemoveButton = styled.button`min-height: 2.15rem; display: flex; align-items: center; justify-content: center; gap: .3rem; border: 0; border-radius: .62rem; background: #fff1ef; color: #a03930; font: inherit; font-size: .68rem; font-weight: 850; cursor: pointer; svg { font-size: .9rem; }`;
const ComparisonSection = styled.section`display: grid; gap: .8rem; padding: 1rem; border: 1px solid #ead8ad; border-radius: 1rem; background: linear-gradient(135deg,#fbf7f1,#fff);`;
const ComparisonHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  > div { display: flex; align-items: center; gap: .65rem; color: #654b1b; } > div > svg { color: #a87619; }
  span { display: grid; gap: .12rem; } small { color: #7d6a45; }
  @media (max-width: 34rem) { align-items: flex-start; flex-direction: column; }
`;
const SwitchLabel = styled.label`display: inline-flex; align-items: center; gap: .4rem; padding: .5rem .7rem; border-radius: 999px; background: #fff; color: #6d511c; font-size: .76rem; font-weight: 850; cursor: pointer; input { accent-color: #b27d19; }`;
const ComparisonFields = styled.div`display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: .8rem; @media (max-width: 34rem) { grid-template-columns: 1fr; }`;
const Fields = styled.div`display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: .9rem; @media (max-width: 38rem) { grid-template-columns: 1fr; }`;
const Field = styled.div`
  display: grid; gap: .35rem; grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
  label { color: #29465a; font-size: .78rem; font-weight: 850; }
  input, textarea, select { width: 100%; box-sizing: border-box; border: 1px solid #e7dfd2; border-radius: .75rem; background: #fff; color: #1f3648; font: inherit; padding: .75rem .8rem; }
  textarea { resize: vertical; line-height: 1.5; }
  input:focus, textarea:focus, select:focus { outline: 3px solid rgba(19,79,119,.12); border-color: #4d7b99; }
  small { justify-self: end; color: #81909a; font-size: .68rem; }
`;
const PublishPanel = styled.section`display: grid; grid-template-columns: minmax(0,1.2fr) minmax(0,1fr); align-items: end; gap: 1rem; padding: 1rem; border-radius: 1rem; background: #faf8f4; @media (max-width: 38rem) { grid-template-columns: 1fr; }`;
const FeaturedLabel = styled.label`display: flex; align-items: flex-start; gap: .65rem; padding: .75rem; border-radius: .8rem; background: #fff; cursor: pointer; input { margin-top: .2rem; accent-color: #c8922c; } span { display: grid; gap: .1rem; } strong { color: #2b475b; font-size: .82rem; } small { color: #6d7c86; line-height: 1.4; }`;
const ErrorMessage = styled.p`margin: 0; padding: .75rem; border: 1px solid #efcec9; border-radius: .75rem; background: #fff5f4; color: #8c342c;`;
const Actions = styled.div`display: flex; justify-content: flex-end; flex-wrap: wrap; gap: .65rem;`;
const BaseButton = styled.button`min-height: 2.75rem; padding: .7rem 1rem; border-radius: .8rem; font: inherit; font-weight: 850; cursor: pointer; &:disabled { opacity: .55; cursor: wait; }`;
const PrimaryButton = styled(BaseButton)`border: 0; background: #111111; color: #fff;`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #e7dfd2; background: #fff; color: #111111;`;

export default PortfolioProjectEditor;
